import { useMemo } from 'react';

import { Prose, Text } from '@blockworks/ui';
import { cn } from '@blockworks/ui/utils';
import { Visualization } from '@blockworks/viz';
import { VisualizationOptionsProvider } from '@blockworks/viz/context';
import { DashboardGrid, PreloadedDashboardData } from '@blockworks/viz/dashboard-grid';

import Breadcrumb from '@/components/display/headers/breadcrumb';
import { VisualizationPageProps } from '@/components/pages/analytics/utils/get-visualization-props';
import { useSocialSettings } from '@/components/pages/analytics/utils/use-social-link';

import { makeExecutionProxyUriGetter } from '../utils/get-execution-proxy-uri';
import { getVisualizationDisplayName } from '../utils/get-visualization-display-name';

const createAboutSection = (visualization: VisualizationPageProps['visualization']) => {
    const description = visualization.description;
    const type = getVisualizationDisplayName(visualization);

    if (type && description) {
        const heading = `## About this ${type}`;
        return `${heading}\n\n${description}`;
    }
    return;
};

const VisualizationPageLayout = ({
    dashboardId,
    datapageName,
    datapageSlug,
    dashboardSlug,
    datapageDashboardTitle,
    preloadedDataProp,
    visualization,
}: VisualizationPageProps) => {
    let preloadedData: PreloadedDashboardData | null;
    try {
        preloadedData = DashboardGrid.decompressData<PreloadedDashboardData>(preloadedDataProp ?? undefined) ?? null;
    } catch (error) {
        console.error('Failed to decompress dashboard data:', error);
        preloadedData = null;
    }

    const slots = useMemo(
        () => ({
            HeaderTitle: ({ title }: { title: string }) => (
                <Text
                    as={'h1'}
                    size="xl"
                    weight="semibold"
                    lines={1}
                    truncate
                    className={cn({ '-mt-1.5': !!visualization.subTitle, '-mt-0.5': !visualization.subTitle })}
                >
                    {title}
                </Text>
            ),
        }),
        [],
    );

    const socialShareConfig = useSocialSettings(dashboardId, `${datapageSlug}/${dashboardSlug}`);
    const aboutSection = createAboutSection(visualization);

    return (
        <div className="pb-6 h-full max-w-[100vw] relative z-[0]">
            <div className="h-max divide-y">
                <div className="px-4">
                    <Breadcrumb
                        className="py-3"
                        items={[
                            { link: '/analytics', title: 'Analytics' },
                            {
                                title: `${datapageName}: ${datapageDashboardTitle}`,
                                link: `/analytics/${datapageSlug}/${dashboardSlug}`,
                            },
                        ]}
                    />
                </div>
                <div className="lg:max-w-full max-w-full min-h-[400px] h-[calc(100vh_-_224px)] max-h-[635px]">
                    <VisualizationOptionsProvider
                        features={{
                            dangerouslyGateDataExport: true,
                            socialMediaSharing: socialShareConfig,
                            getExecutionProxyUri: makeExecutionProxyUriGetter(dashboardId),
                        }}
                        layout={{ headerHeight: 60 }}
                    >
                        {!preloadedData && (
                            <Visualization.Obscured visualization={visualization} componentSlots={slots} />
                        )}
                        {preloadedData && (
                            <Visualization.PreloadedWithExecution
                                visualization={preloadedData[0]}
                                componentSlots={slots}
                            />
                        )}
                    </VisualizationOptionsProvider>
                </div>
                <div className={cn('lg:px-6 px-10', { 'pt-6': !aboutSection })}>
                    {!!aboutSection && (
                        <Prose.Root
                            size="sm"
                            id={`${visualization.id}-description`}
                            content={aboutSection}
                        ></Prose.Root>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VisualizationPageLayout;
